/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { Query } from '@/gql/graphql'
import { gql, useQuery } from '@apollo/client'
import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const GET_JOB_REQUEST_TYPES_QUERY = gql`
  query {
    jobRequestTypes(status: active) {
      id
      description
      status
      createdAt
      updatedAt
    }
    stores(isVirtual: false, status: active) {
      id
      rhBranchId
      name
    }
  }
`

const VALID_STATUS = [
  'requested',
  'canceled',
  'deleted',
  'approved',
  'rejected',
  'closed',
  'pending',
  'in_process'
] as const

const validationSchema = yup.object().shape({
  department: yup.string()
    .min(1, 'O setor deve ter no mínimo 1 caractere')
    .max(255, 'O setor deve ter no máximo 255 caracteres'),
  indication: yup.string().max(255, 'A indicação deve ter no máximo 255 caracteres').default(''),
  role: yup.string()
    .min(1, 'A função deve ter no mínimo 1 caractere')
    .max(255, 'A função deve ter no máximo 255 caracteres'),
  reason: yup.string()
    .min(1, 'O motivo deve ter no mínimo 1 caractere')
    .max(3000, 'O motivo deve ter no máximo 255 caracteres'),
  priority: yup.number()
    .positive()
    .min(0, 'A prioridade deve ser um numero positivo')
    .max(20, 'A prioridade deve ser menor ou igual a 100')
    .default(0),
  amount: yup.number().positive()
    .min(1, 'A quantidade deve ser um numero positivo')
    .max(1000, 'A quantidade deve ser menor ou igual a 1000')
    .default(1),
  workload: yup.string()
    .max(255, 'Os detalhes da carga horária deve ter no máximo 255 caracteres')
    .default(''),
  gender: yup.string()
    .oneOf(['male', 'female', 'other', 'any'], 'O gênero deve ser uma das opções válidas')
    .nullable(),
  status: yup.string()
    .oneOf(VALID_STATUS, 'A situação deve ser uma das opções válidas')
    .default('requested'),
  origin: yup.string()
    .oneOf(['internal', 'external', 'temporary', 'permanent'], 'A origem deve ser uma das opções válidas').nullable(),
  requester: yup.object().shape({
    id: yup.string().nullable(),
    name: yup.string().nullable()
  }).nullable().default(null),
  store: yup.object().shape({
    id: yup.string().nullable(),
  }).nullable().default(null),
  requestType: yup.object().shape({
    id: yup.string().nullable()
  }).nullable().default(null),
  createdAt: yup.string(),
  updatedAt: yup.string()
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormJobRequestHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormJobRequestHandleSubmit
  initialValues?: Partial<ValidationSchema>
  isLoading?: boolean
  readOnly?: boolean
  adminMode?: boolean
  reviewMode?: boolean
}

/**
 * FormJobRequest is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormJobRequest from './src/views/PostEdit/FormJobRequest.tsx'
 *
 * const MyComponent = () => (
 *   <FormJobRequest />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormJobRequest: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false,
  readOnly = false,
  adminMode = false,
  reviewMode = false
}) => {
  const navigate = useNavigate()
  const { data, loading, called } = useQuery<Query>(GET_JOB_REQUEST_TYPES_QUERY)
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  const sortedStores = useMemo(() => {
    return data?.stores?.slice()?.sort((a, b) => {
      const nameA = Number.parseInt(a?.rhBranchId ?? '') || 0
      const nameB = Number.parseInt(b?.rhBranchId ?? '') || 0
      return nameA - nameB
    }) ?? []
  }, [data?.stores, loading, called])

  if (loading && !called) {
    return (
      <p>Loading...</p>
    )
  }

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row style={{ paddingBottom: 20 }}>
        {formik?.values?.requester !== null && (
          <>
            <Form.Group as={Col} md={6} className='mb-3'>
              <FloatingLabel label="Solicitante da vaga" className="mb-3">
                <Form.Control
                  type='text'
                  name='requester.name'
                  value={formik?.values?.requester?.name ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group as={Col} md={6} className='mb-3'>
              <FloatingLabel label="Modificado em" className="mb-3">
                <Form.Control
                  type='datetime-local'
                  name='createdAt'
                  value={formik?.values?.updatedAt ?? formik?.values?.createdAt ?? ''}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled // ={formik.isSubmitting || isLoading || readOnly}
                />
              </FloatingLabel>
            </Form.Group>
          </>
        )}
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Informe o setor (máx.: 255 caracteres)" className="mb-3">
            <Form.Control
              type='text'
              name='department'
              maxLength={255}
              value={formik?.values?.department ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Informe a função (máx.: 255 caracteres)" className="mb-3">
            <Form.Control
              type='text'
              name='role'
              maxLength={255}
              value={formik?.values?.role ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Quantidade de vagas" className="mb-3">
            <Form.Control
              type='number'
              name='amount'
              maxLength={255}
              value={formik?.values?.amount ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Carga horária" className="mb-3">
            <Form.Control
              type='text'
              name='workload'
              maxLength={255}
              value={formik?.values?.workload ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Indicação (opcional)" className="mb-3">
            <Form.Control
              type='string'
              name='indication'
              maxLength={255}
              value={formik?.values?.indication ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
            />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Gênero" className="mb-3">
            <Form.Select
              value={formik.values.gender ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
              name="gender"
            >
              <option>Selecione uma opção</option>
              <option value='male'>Masculino</option>
              <option value='female'>Feminino</option>
              <option value='other'>Outro</option>
              <option value='any'>Qualquer</option>
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Origem" className="mb-3">
            <Form.Select
              value={formik.values.origin ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
              name="origin"
            >
              <option>Selecione uma opção</option>
              <option value='internal'>Interna</option>
              <option value='external'>Externa</option>
              <option value='temporary'>Temporária</option>
              <option value='permanent'>Efetivação</option>
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Motivo da abertura da vaga" className="mb-3">
            <Form.Select
              value={formik.values?.requestType?.id ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
              name="requestType.id"
            >
              <option>Selecione uma opção</option>
              {data?.jobRequestTypes?.map(type => (
                <option key={type.id} value={type.id}>
                  {type.description}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Loja (opcional)" className="mb-3">
            <Form.Select
              value={formik.values?.store?.id ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
              name="store.id"
            >
              <option>Selecione uma opção</option>
              {sortedStores?.map(store => (
                <option key={store?.id} value={store?.id}>
                  {store?.rhBranchId ? `${store?.rhBranchId} - ${store?.name}` : store?.name}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Situação" className="mb-3">
            <Form.Select
              value={formik.values.status ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || !reviewMode}
              name="status"
            >
              <option disabled selected>Selecione uma opção</option>
              <option value='pending'>Solicitado</option>
              <option value='in_process'>Em andamento</option>
              <option value='approved'>Aprovada</option>
              <option value='rejected'>Rejeitada</option>
            </Form.Select>
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Descreva o motivo da abertura da vaga" className="mb-3">
            <Form.Control
              as="textarea"
              rows={9}
              name='reason'
              maxLength={3000}
              style={{ height: 'auto' }}
              value={formik?.values?.reason ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly || !adminMode}
            />
          </FloatingLabel>
        </Form.Group>
        <Col className='mb-3' md={12}>
          <Button
            variant='outline-link'
            style={{ marginRight: 10 }}
            onClick={() => navigate('/job-requests')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type='submit'
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default FormJobRequest
